import {
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { FileDown } from 'lucide-react';

import { LAYER_NORM_NONE } from '../../../constants/layers.ts';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { userReducer } from '../../../store/reducers/user.reducer';
import { getUserFeatureFlags } from '../../../store/selectors/featureFlags.selector';
import { getNorms } from '../../../store/selectors/user.selector';
import { selectActiveVersion } from '../../../store/selectors/workspace.selector.ts';
import { FRONTEND_PDF_PLOTS_FLAG, MEASUREMENT_SURFACES_FLAG } from '../../../utils/featureFlagKeys';
import {
  canExportPdf,
  isCalculationCompleted,
  isCalculationStarted,
  isJobFailed,
  isJobInProgress,
} from '../../../utils/jobStatus.ts';
import { isFullyCatalogueAssigned, isMarketMatchedVersion } from '../../../utils/lampMarketMatch';
import { selectWorkspaceMeasurementData } from '../../../utils/measurementCalculation/measurementSource';
import LayersIcon from '../icons/LayersIcon';
import LightingIcon from '../icons/LightingIcon';
import MarketMatchIcon from '../icons/MarketMatchIcon';
import ResultsIcon from '../icons/ResultsIcon';
import SurfacesIcon from '../icons/SurfacesIcon';
import { MeasurementSurfacesPanel } from '../sidebar/MeasurementSurfacesPanel.tsx';
import { PanelFooter } from './PanelFooter';
import { PanelRail, type RailItem } from './PanelRail';
import { PanelSection } from './PanelSection';
import { isPanelTab } from './panelUrl';
import { ExportSection } from './sections/ExportSection';
import { LayersSection } from './sections/LayersSection';
import { LuminairesSection } from './sections/LuminairesSection';
import { MarketMatchSection } from './sections/MarketMatchSection';
import { ResultsSection } from './sections/ResultsSection';
import { DISTANCE_TO_GROUND_NA, type PanelHighlight, type PanelSectionId } from './types';
import { useWorkspaceNextStep } from './useWorkspaceNextStep';
import { useWorkspaceShortcuts } from './useWorkspaceShortcuts';

const PREFS_KEY = 'taman.workspacePanel';
const MIN_WIDTH = 300;
const MAX_WIDTH = 440;
const DEFAULT_WIDTH = 340;

interface PanelPrefs {
  open: boolean;
  width: number;
}

function readPrefs(): PanelPrefs {
  try {
    const raw = JSON.parse(localStorage.getItem(PREFS_KEY) || '{}');
    const width = Number(raw.width);
    return {
      open: raw.open !== false,
      width: Number.isFinite(width) ? Math.min(Math.max(width, MIN_WIDTH), MAX_WIDTH) : DEFAULT_WIDTH,
    };
  } catch {
    return { open: true, width: DEFAULT_WIDTH };
  }
}

function writePrefs(prefs: PanelPrefs) {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
  } catch {
    // Private mode / blocked storage: the panel just won't remember.
  }
}

interface WorkspaceSidePanelProps {
  /** The export view (PDF preview on the canvas) is open; it maps to the Export section. */
  exportsOpen: boolean;
  onExportsChange: (open: boolean) => void;
}

/**
 * The workspace side panel (layout v2), docked on the right: [section panel][icon rail].
 * One section at a time, one scroll, and a fixed footer that says what is missing and offers
 * the next step. Collapsing keeps only the rail, so the 3D view gets the width back.
 */
export function WorkspaceSidePanel({ exportsOpen, onExportsChange }: WorkspaceSidePanelProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [prefs, setPrefs] = useState(readPrefs);
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab');
  // Every version opens on Layers, unless a shared link names a section (`tab=results`).
  const [section, setSection] = useState<PanelSectionId>(() => (isPanelTab(initialTab) ? initialTab : 'layers'));
  // That section may not be available yet (results still streaming in): keep it in the URL until it opens.
  const urlIntent = useRef<PanelSectionId | null>(
    isPanelTab(initialTab) && initialTab !== 'layers' ? initialTab : null,
  );
  const [highlight, setHighlight] = useState<PanelHighlight>(null);
  const [marketCanStart, setMarketCanStart] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const highlightTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const version = useAppSelector(selectActiveVersion);
  const lamps = useAppSelector((s) => s.workspace.lamps);
  const modelLayers = useAppSelector((s) => s.workspace.modelLayers);
  const selectedLayer = useAppSelector((s) => s.workspace.selectedLayer);
  const selectedLampId = useAppSelector((s) => s.workspace.lamps.find((l) => l.isSelected)?.id ?? null);
  const applied = useAppSelector((s) => s.workspace.appliedLampSimilaritySelection);
  const planeCount = useAppSelector((s) => s.measurementPlanes.planes.length);
  const flags = useAppSelector(getUserFeatureFlags);
  const measurementData = useAppSelector(selectWorkspaceMeasurementData);
  const norms = useSelector(getNorms);

  useWorkspaceShortcuts(!exportsOpen);

  // Norms feed the layer table and the results; the legacy sidebar loaded them the same way.
  const normsRequested = useRef(false);
  useEffect(() => {
    if ((!norms || norms.length === 0) && !normsRequested.current) {
      normsRequested.current = true;
      dispatch(userReducer.actions.loadNorms());
    }
  }, [dispatch, norms]);

  const updatePrefs = useCallback((next: Partial<PanelPrefs>) => {
    setPrefs((prev) => {
      const merged = { ...prev, ...next };
      writePrefs(merged);
      return merged;
    });
  }, []);

  // ── Section availability (also drives the rail) ─────────────────────────────
  const calcCompleted = isCalculationCompleted(version);
  const marketMatched = isMarketMatchedVersion(version);
  const fullyAssigned = isFullyCatalogueAssigned(version);
  const hasResults =
    (calcCompleted || fullyAssigned || applied?.previewMode === 'frontend') && !!measurementData?.length;
  const measurementAvailable =
    (calcCompleted && !!version?.measurementFileId) || fullyAssigned || (!!applied && !!measurementData?.length);
  const surfacesEnabled = flags.includes(MEASUREMENT_SURFACES_FLAG);
  const exportEnabled = canExportPdf(version, { frontendPdfPlotsEnabled: flags.includes(FRONTEND_PDF_PLOTS_FLAG) });

  const railItems = useMemo<RailItem[]>(() => {
    const setupOpen = !isCalculationStarted(version);
    const lmn = modelLayers.filter((l) => l.isIlluminated);
    const layersIncomplete =
      setupOpen &&
      (lmn.length === 0 ||
        lmn.some((l) => !l.selectedNorm || l.selectedNorm === LAYER_NORM_NONE) ||
        (lamps.length === 0 && !modelLayers.some((l) => l.canHoldLuminaire)));
    const lampsNotAbove = lamps.some((l) => l.distanceToGround === DISTANCE_TO_GROUND_NA);
    const candidates = version?.lampSimilarityResult?.candidates ?? [];

    const items: (RailItem | false)[] = [
      {
        id: 'layers',
        label: t('workspace.panel.rail.layers'),
        icon: <LayersIcon size={20} />,
        // Layer count, amber while the setup still needs something on this tab.
        badge:
          modelLayers.length > 0
            ? { tone: layersIncomplete ? 'warning' : 'count', text: String(modelLayers.length) }
            : undefined,
        hint: layersIncomplete ? t('workspace.panel.layersNeedAttention') : undefined,
      },
      {
        id: 'luminaires',
        label: t('workspace.panel.rail.luminaires'),
        icon: <LightingIcon size={20} />,
        // Luminaire count, red while one of them is not above the model.
        badge: lamps.length > 0 ? { tone: lampsNotAbove ? 'error' : 'count', text: String(lamps.length) } : undefined,
        hint: lampsNotAbove ? t('workspace.panel.lampsNeedAttention') : undefined,
      },
      {
        id: 'results',
        label: t('workspace.panel.rail.results'),
        icon: <ResultsIcon size={18} />,
        disabled: !hasResults,
        hint: hasResults ? undefined : t('workspace.panel.resultsLocked'),
        badge: hasResults ? { tone: 'done' } : undefined,
      },
      surfacesEnabled && {
        id: 'surfaces',
        label: t('workspace.panel.rail.surfaces'),
        icon: <SurfacesIcon size={20} />,
        disabled: !measurementAvailable,
        hint: measurementAvailable ? undefined : t('workspace.panel.surfacesLocked'),
        badge: planeCount > 0 ? { tone: 'count', text: String(planeCount) } : undefined,
      },
      !marketMatched && {
        id: 'market',
        label: t('workspace.panel.rail.market'),
        icon: <MarketMatchIcon size={20} />,
        group: 'finish',
        disabled: !calcCompleted,
        hint: calcCompleted ? undefined : t('workspace.panel.marketLocked'),
        badge: isJobInProgress(version?.lampSimilarityJob)
          ? { tone: 'busy' }
          : isJobFailed(version?.lampSimilarityJob)
            ? { tone: 'error', text: '!' }
            : candidates.length > 0
              ? { tone: 'count', text: String(candidates.length) }
              : undefined,
      },
      {
        id: 'export',
        label: t('workspace.panel.rail.export'),
        icon: <FileDown size={20} />,
        // Export swaps the 3D view for the PDF preview, so it sits apart at the bottom of the rail.
        group: 'mode',
        disabled: !exportEnabled,
        hint: !exportEnabled
          ? t('workspace.export.notAvailable')
          : exportsOpen
            ? t('workspace.panel.exportBack')
            : t('workspace.panel.exportHint'),
      },
    ];
    return items.filter((item): item is RailItem => !!item);
  }, [
    t,
    version,
    modelLayers,
    lamps,
    hasResults,
    measurementAvailable,
    surfacesEnabled,
    planeCount,
    marketMatched,
    calcCompleted,
    exportEnabled,
    exportsOpen,
  ]);

  const isAvailable = (id: PanelSectionId) => railItems.some((item) => item.id === id && !item.disabled);
  const activeSection: PanelSectionId = exportsOpen ? 'export' : isAvailable(section) ? section : 'layers';

  // ── Navigation ───────────────────────────────────────────────────────────────
  const goTo = useCallback(
    (id: PanelSectionId, flash: PanelHighlight = null) => {
      urlIntent.current = null;
      if (id === 'export') {
        onExportsChange(true);
      } else {
        if (exportsOpen) onExportsChange(false);
        setSection(id);
      }
      updatePrefs({ open: true });
      scrollRef.current?.scrollTo({ top: 0 });
      if (highlightTimer.current) clearTimeout(highlightTimer.current);
      setHighlight(flash);
      if (flash) highlightTimer.current = setTimeout(() => setHighlight(null), 1500);
    },
    [exportsOpen, onExportsChange, updatePrefs],
  );

  useEffect(
    () => () => {
      if (highlightTimer.current) clearTimeout(highlightTimer.current);
    },
    [],
  );

  const selectFromRail = (id: PanelSectionId) => {
    // Export is a mode: clicking it again goes back to the 3D view and the section before it.
    if (id === 'export' && exportsOpen && prefs.open) {
      onExportsChange(false);
      return;
    }
    // Clicking the open section again collapses the panel, like Slack.
    if (prefs.open && id === activeSection) {
      updatePrefs({ open: false });
      return;
    }
    goTo(id);
  };

  // Rail shortcuts (physical keys, so they work on any keyboard layout): ⌥1…⌥n picks a
  // section, ⌥0 hides or shows the panel.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (!e.altKey || e.metaKey || e.ctrlKey || !/^Digit[0-9]$/.test(e.code)) return;
      if (e.code === 'Digit0') {
        e.preventDefault();
        updatePrefs({ open: !prefs.open });
        return;
      }
      const item = railItems[Number(e.code.slice(5)) - 1];
      if (!item || item.disabled) return;
      e.preventDefault();
      goTo(item.id);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [railItems, goTo, prefs.open, updatePrefs]);

  // Switching to another version starts on Layers again; the panel never jumps to Results by itself.
  const lastVersionId = useRef(version?.id);
  useEffect(() => {
    if (version?.id == null) return;
    if (lastVersionId.current != null && lastVersionId.current !== version.id) {
      urlIntent.current = null;
      setSection('layers');
    }
    lastVersionId.current = version.id;
  }, [version?.id]);

  // Keep `tab` in the URL in step with the open section, so the link can be shared.
  useEffect(() => {
    if (exportsOpen) return;
    if (urlIntent.current) {
      if (urlIntent.current !== activeSection) return;
      urlIntent.current = null;
    }
    if (searchParams.get('tab') === activeSection) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', activeSection);
        return next;
      },
      { replace: true },
    );
  }, [activeSection, exportsOpen, searchParams, setSearchParams]);

  // While setting up (Layers / Luminaires), picking a layer or a luminaire in 3D brings up its
  // section. Results, surfaces, market and export stay put: there a pick is just a highlight.
  const inSetup = !exportsOpen && (section === 'layers' || section === 'luminaires');
  const lastLayer = useRef(selectedLayer);
  useEffect(() => {
    if (selectedLayer && selectedLayer !== lastLayer.current && inSetup) setSection('layers');
    lastLayer.current = selectedLayer;
  }, [selectedLayer, inSetup]);
  const lastLamp = useRef(selectedLampId);
  useEffect(() => {
    if (selectedLampId && selectedLampId !== lastLamp.current && inSetup) setSection('luminaires');
    lastLamp.current = selectedLampId;
  }, [selectedLampId, inSetup]);

  const step = useWorkspaceNextStep({ activeSection, goTo, marketCanStart });

  // ── Resize (drag the panel's left edge) ─────────────────────────────────────
  const startResize = (e: ReactPointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = prefs.width;
    const handle = e.currentTarget;
    handle.setPointerCapture(e.pointerId);
    let width = startWidth;
    const onMove = (ev: PointerEvent) => {
      width = Math.min(Math.max(startWidth + (startX - ev.clientX), MIN_WIDTH), MAX_WIDTH);
      setPrefs((prev) => ({ ...prev, width }));
    };
    const onUp = () => {
      handle.removeEventListener('pointermove', onMove);
      handle.removeEventListener('pointerup', onUp);
      handle.removeEventListener('pointercancel', onUp);
      updatePrefs({ width });
    };
    handle.addEventListener('pointermove', onMove);
    handle.addEventListener('pointerup', onUp);
    handle.addEventListener('pointercancel', onUp);
  };

  const content = (() => {
    switch (activeSection) {
      case 'layers':
        return <LayersSection highlightToggles={highlight === 'toggles'} />;
      case 'luminaires':
        return <LuminairesSection />;
      case 'results':
        return <ResultsSection />;
      case 'surfaces':
        return (
          <PanelSection title={t('workspace.panel.surfaces.title')} count={planeCount || undefined}>
            <MeasurementSurfacesPanel embedded />
          </PanelSection>
        );
      case 'market':
        return <MarketMatchSection onCanStartChange={setMarketCanStart} />;
      case 'export':
        return <ExportSection />;
    }
  })();

  return (
    // On phones the panel floats over the 3D view, from the page's left edge up to the rail
    // (the page row is its containing block there); from md up it sits in the row at its width.
    <div className="flex h-full shrink-0 md:relative">
      {prefs.open && (
        <div
          className="flex flex-col h-full min-h-0 bg-[#232323] border-l border-white/10 max-md:absolute max-md:inset-y-0 max-md:left-0 max-md:right-16 max-md:z-[60] max-md:shadow-2xl md:relative md:w-[var(--panel-width)]"
          style={{ '--panel-width': `${prefs.width}px` } as CSSProperties}
        >
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label={t('workspace.panel.resize')}
            title={t('workspace.panel.resize')}
            onPointerDown={startResize}
            onDoubleClick={() => updatePrefs({ width: DEFAULT_WIDTH })}
            className="absolute inset-y-0 -left-1 z-20 w-2 cursor-col-resize hidden md:block after:absolute after:inset-y-0 after:left-[3px] after:w-0.5 after:bg-transparent hover:after:bg-lime-400/60 active:after:bg-lime-400"
          />
          <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto overscroll-contain workspace-panel-scroll">
            {content}
          </div>
          <PanelFooter step={step} />
        </div>
      )}
      <PanelRail
        items={railItems}
        active={activeSection}
        open={prefs.open}
        onSelect={selectFromRail}
        onToggle={() => updatePrefs({ open: !prefs.open })}
      />
    </div>
  );
}
